import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useChargebackStore } from '../stores/chargebackStore';

// Wizard Steps
import Step1_OemPeriod from '../components/chargeback/Step1_OemPeriod';
import Step2_PartSelection from '../components/chargeback/Step2_PartSelection';
import Step3_Review from '../components/chargeback/Step3_Review';

export default function ChargebackGenerator() {
  const navigate = useNavigate();
  const { currentStep, setStep, reset } = useChargebackStore();

  useEffect(() => {
    // Reset wizard when component mounts
    return () => {
      // Optional: keep state when navigating away
    };
  }, []);

  const handleNext = () => {
    setStep(currentStep + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const handleComplete = () => {
    // Show success message and redirect
    setTimeout(() => {
      reset();
      navigate('/dashboard');
    }, 1000);
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      reset();
      navigate('/dashboard');
    }
  };

  const steps = [
    { number: 1, title: 'OEM & Period', description: 'Select customer and timeframe' },
    { number: 2, title: 'Select Parts', description: 'Choose parts to include' },
    { number: 3, title: 'Review & Generate', description: 'Generate PDF chargeback' }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={16} className="mr-1" />
              Cancel
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mt-2">Chargeback Generator</h1>
          <p className="mt-2 text-gray-600">
            Create a professional PDF chargeback for OEM cost recovery
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between">
          {steps.map((step, idx) => (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                {/* Step Circle */}
                <div
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg
                    transition-colors duration-200
                    ${currentStep > step.number
                      ? 'bg-green-600 text-white'
                      : currentStep === step.number
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                    }
                  `}
                >
                  {currentStep > step.number ? (
                    <Check size={24} />
                  ) : (
                    step.number
                  )}
                </div>
                {/* Step Label */}
                <div className="mt-2 text-center">
                  <p className={`
                    text-sm font-semibold
                    ${currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'}
                  `}>
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-500">{step.description}</p>
                </div>
              </div>

              {/* Connector Line */}
              {idx < steps.length - 1 && (
                <div className="flex-1 h-1 mx-4 mt-[-40px]">
                  <div
                    className={`
                      h-full transition-colors duration-200
                      ${currentStep > step.number ? 'bg-green-600' : 'bg-gray-200'}
                    `}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-[600px]">
        {currentStep === 1 && <Step1_OemPeriod onNext={handleNext} />}
        {currentStep === 2 && (
          <Step2_PartSelection onNext={handleNext} onBack={handleBack} />
        )}
        {currentStep === 3 && (
          <Step3_Review onBack={handleBack} onComplete={handleComplete} />
        )}
      </div>
    </div>
  );
}
